import { IAlertState } from '@store/alerts/types';
import { createDeferredAction } from '@store/common/actions';
import { httpRequestFactory } from '@store/common/http-request-factory';
import { HttpRequestMethod } from '@store/common/http-request-method';
import { MAIN_API } from '@store/common/path';
import { languagesSelector } from '@store/languages/selectors';
import { AxiosResponse, HttpStatusCode } from 'axios';
import { logError } from 'core/sentry';
import { ILanguage, IUpdateNotificationPreference } from 'lingopractices-models';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { UpdateAlertFailure } from './update-alert-failure';
import { UpdateAlertSuccess } from './update-alert-success';

export class UpdateAlert {
  static get action() {
    return createDeferredAction<IUpdateNotificationPreference>(
      'notifications/UPDATE_ALERT_PREFERENCES',
    );
  }

  static get reducer() {
    return (draft: IAlertState) => {
      draft.requests.updateNotificationsPending = true;
      return draft;
    };
  }

  static get saga() {
    return function* ({ payload, meta }: ReturnType<typeof UpdateAlert.action>): SagaIterator {
      const languages: ILanguage[] = yield select(languagesSelector);

      try {
        const response = UpdateAlert.httpRequest.call(
          yield call(() => UpdateAlert.httpRequest.generator(payload)),
        );

        if (!response) {
          return;
        }

        const alertLanguage = languages.find(
          (language) => language.id === payload.languageId,
        ) as ILanguage;

        if (response.status === HttpStatusCode.Ok) {
          yield put(
            UpdateAlertSuccess.action({
              ...payload,
              language: alertLanguage,
            }),
          );
          meta?.deferred.resolve();
        }
      } catch (e: any) {
        yield put(UpdateAlertFailure.action());
        meta?.deferred.reject(e);
        logError(e);
      }
    };
  }

  static get httpRequest() {
    return httpRequestFactory<AxiosResponse<undefined>, IUpdateNotificationPreference>(
      MAIN_API.NOTIFICATIONS_PREFERENCES,
      HttpRequestMethod.Put,
    );
  }
}
